import React from 'react';
import { Box, Typography } from '@mui/material';
import ApiParametersTable from '../ApiParametersTable';
import ApiEndpointDisplay from '../ApiEndpointDisplay';
import ApiResponseExamples from '../ApiResponseExamples';
import ApiRequestExamples from '../ApiRequestExamples';

const SdVideoDownloadApi: React.FC = () => {
    const parameters = [
        { type: 'HEAD', name: 'Authorization', dataType: 'string', required: true },
        { type: 'POST', name: 'vehicleId', dataType: 'string', required: true },
        { type: 'POST', name: 'cameraId', dataType: 'string', required: true },
        { type: 'POST', name: 'startDateTime', dataType: 'string', required: true },
        { type: 'POST', name: 'endDateTime', dataType: 'string', required: true },
        { type: 'POST', name: 'blurEnable', dataType: 'boolean', required: true },
    ];

    return (
        <Box className="flex flex-col border-b border-gray-200">
            <Typography variant="h6" marginBottom={1} fontWeight={600} fontSize={16} id="sd-video-download">
               DOWNLOAD SD VIDEO ON DEMAND
            </Typography>
            
            <Typography variant='body2' marginBottom={2} fontSize={14} fontWeight={400}>
                To download a video from the SD card for a specific vehicle, you must send a POST request. This request requires an Authorization token in the headers and a request body containing the details of the vehicle for which you want to download the video.
            </Typography>

            {/* Request Section */}
            <Typography variant='body2' marginBottom={1} fontSize={14} fontWeight={600}>Request</Typography>
            <ApiEndpointDisplay 
                method="POST" 
                url="https://backend.novusomnifleet.com/api/client/v1/download-sd-card" 
            />

            <ApiParametersTable parameters={parameters} />

            <ApiRequestExamples
                examples={[
                    {
                    type: "headers",
                    headers: [
                        {
                        key: "Authorization",
                        value:
                            "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJJQ1NBIiwidXNlcklkIjoiNjdkOTBkMDdlNGNhZTU3MDEwMzQyYzA4Iiwicm9sZXMiOiJST0xFX0ZMRUVUX01BTkFHRVIiLCJpYXQiOjE3NTQzNzIyNjksImV4cCI6MTc1NDQwODI2OX0.NItAkAwXNGKxoAsUTZSwB0lzFPHgW3I4ZwpLEY4c-Lo"
                        }
                    ],
                    label: "Request Headers"
                    },
                    {
                    type: "body",
                    contentType: "json",
                    value: `{
 "vehicleId": "S1234",
 "cameraId": "01",
 "startDateTime": "2025-06-05:13:04:48",
 "endDateTime": "2025-07-07:00:07:18",
 "blurEnable": false
}`,
                    label: "Request Body"
                    }
                ]}
            />

            <ApiResponseExamples 
                examples={[
                    {
                        status: '200',
                        description: 'Success',
                        response: `{
                                      "data": {
                                        "cameraId": "2",
                                        "playBackurl": "https://bucketName.s3.ap-south-1.amazonaws.com/sd-card/8631823/2025-07-314031823/2_863386023_1753930510.mp4?X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKb%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCmFwLXNvdXRoLTEiRzBFAiAhV%2F8hv6K49%2FbH%2Fnx1BSrj88FwULw17i0cymngdetTyAIhAKhuML9BwksTJI9XidSEcFX02WUTBSViphCMkfCmTcC1KvkCCM%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMTkxNzUzNDU5MDE4IgxE4FaMbtsccEt%2FJ8EqzQKu10yIG8OuJMy3Wo2jYYQSsfLgxRKOZXup%2BGfvwFvGuTuZFy6L6ao5G31Lefk3XOEMkkIpNKJw4CBh5nBlRa3yHhRYFQY%2B9WWGmtVpS7BsLparactDIMYKBL9EcPQDXisj5h0%2FiHgdPTSD4WjskkQw%2B7uMz6prRYhQLPi%2Bh3X0KsAAaOJFP3%2FlRO0jYPyzIgPhd41mHldPNdFSvhiZyOU5rocepkp2NDBIC5f8cSHhjxC3LW7cBKckkQwzI4Iaj07Koo6%2FsjaD9GJMjbA5UY%2B2kVszJCw90H3jVg%2BcEyXLgKqC8z3NAxwAgXn0ch6sCNc2Ek7kbTeA91VuC88lVF7%2BXXSULLcL1UkcHb8kwu4CsxAY6pwGC6sVu0GtA3kD1qKXHc5wH60LiXeOOMq01zwKQPoE1VZ93Pe73%2FMeit%2F9J33gSeDm8cF8ZyiE65I7gGQus6iLDfoG3lpujmBE3T7S%2Fzsy0fgY%2B%2BFMi09XcA83g9OZT78a5nXqsgUFXlPKaH436FZ8MGRAHmSIYR3wRsvBn7X4T%2FG3g2eX5VEo5Hmez9U2bv3emw%3D%3D&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20250731T085959Z&X-Amz-SignedHeaders=host&X-Amz-Expires=604800&X-Amz-Credential=ASIASZJLFSVFGSXOD2BA%2F20250731%2Fap-south-1%2Fs3%2Faws4_request&X-Amz-Signature=66cdf7eddae0e9b82a6680f6f006dc2468fcf780d286160a6281f46b40995630",
                                        "cameraName": "DMS"
                                      },
                                      "message": "Sd Card File downloaded Successfully",
                                      "status": 200
                                    }`
                    },
                    {
                        status: '400',
                        description: 'Missing vehicleId',
                        response: `{
                                      "success": false,
                                      "message": "Invalid request body",
                                      "errors": [
                                        {
                                          "code": "missing_field",
                                          "field": "vehicleId",
                                          "detail": "VehicleId is required"
                                        }
                                      ]
                                    }`
                    },
                    {
                        status: '400',
                        description: 'Missing startDateTime',
                        response: `{
                                      "success": false,
                                      "message": "Invalid request body",
                                      "errors": [
                                        {
                                          "code": "missing_field",
                                          "field": "startDateTime",
                                          "detail": "startDateTime is required"
                                        }
                                      ]
                                    }`
                    },
                    {
                        status: '401',
                        description: 'Unauthorized',
                        response: `{
                                      "success": false,
                                      "message": "Invalid username or password"
                                    }`
                    },
                    {
                        status: '500',
                        description: 'Server Error',
                        response: `{
                                      "success": false,
                                      "message": "An internal server error occurred"
                                    }`
                    }
                ]}
            />
        </Box>
    );
};

export default SdVideoDownloadApi;
